import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { ErrorStateMatcher } from "@angular/material/core";
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from "@angular/material/autocomplete";
import { ControlValueAccessor, FormControl, NgControl } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-searchable-select",
  templateUrl: "./searchable-select.component.html",
  styleUrls: ["./searchable-select.component.scss"],
  standalone: false,
})
export class SearchableSelectComponent<T = unknown>
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  @Input() label = "";
  @Input() options: readonly T[] | null = [];
  @Input() displayWith: (item: T) => string = (item) => String(item ?? "");
  @Input() valueWith: (item: T) => unknown = (item) => item;
  @Input() searchWith: ((item: T) => string) | null = null;
  @Input() imageWith: ((item: T) => string | null | undefined) | null = null;
  @Input() emptyLabel = "";
  @Input() emptyValue: unknown = null;
  @Input() loading = false;
  @Input() disabled = false;
  @Input() required = false;
  @Input() appearance: "fill" | "outline" = "outline";
  @Input() subscriptSizing: "fixed" | "dynamic" = "dynamic";
  @Input() errorStateMatcher?: ErrorStateMatcher;
  @Input()
  set value(next: unknown) {
    this.writeValue(next);
  }

  @Output() valueChange = new EventEmitter<unknown>();
  @Output() opened = new EventEmitter<void>();

  @ViewChild(MatAutocompleteTrigger)
  private readonly autocompleteTrigger?: MatAutocompleteTrigger;

  readonly searchControl = new FormControl("", { nonNullable: true });
  filtered: T[] = [];
  selectedValue: unknown = null;

  private readonly destroy$ = new Subject<void>();
  private readonly defaultErrorStateMatcher = new ErrorStateMatcher();
  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshFiltered());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["options"]) {
      this.refreshFiltered();
      this.syncSearchFromValue();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: unknown): void {
    this.selectedValue = value ?? this.emptyValue;
    this.syncSearchFromValue();
    this.refreshFiltered();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
      return;
    }
    this.searchControl.enable({ emitEvent: false });
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selected = event.option.value as T | null | undefined;
    if (selected == null) {
      if (this.emptyLabel) {
        this.commitValue(this.emptyValue);
      }
      return;
    }
    this.commitValue(this.valueWith(selected));
  }

  onInputClick(): void {
    if (this.disabled) {
      return;
    }
    this.opened.emit();
    const currentLabel = this.currentDisplayLabel();
    if (currentLabel && this.searchControl.value === currentLabel) {
      this.searchControl.setValue("", { emitEvent: true });
    }
    this.autocompleteTrigger?.openPanel();
  }

  onChevronClick(): void {
    this.onInputClick();
  }

  onInputBlur(): void {
    this.onTouched();
    this.syncSearchFromValue();
  }

  trackByIndex(index: number): number {
    return index;
  }

  selectedImageUrl(): string | null {
    if (!this.imageWith) {
      return null;
    }
    const selected = (this.options ?? []).find(
      (item) => this.valueWith(item) === this.selectedValue,
    );
    if (!selected) {
      return null;
    }
    return this.imageWith(selected) || null;
  }

  showInvalidState(): boolean {
    const control = this.ngControl?.control;
    if (!control?.invalid) {
      return false;
    }
    const matcher = this.errorStateMatcher ?? this.defaultErrorStateMatcher;
    return matcher.isErrorState(control as FormControl, null);
  }

  showRequiredMarker(): boolean {
    if (this.required) {
      return true;
    }
    const control = this.ngControl?.control;
    if (!control?.validator) {
      return false;
    }
    return !!control.validator(new FormControl())?.["required"];
  }

  private commitValue(next: unknown): void {
    this.selectedValue = next;
    this.syncSearchFromValue();
    this.onChange(next);
    this.valueChange.emit(next);
    this.onTouched();
  }

  private syncSearchFromValue(): void {
    this.searchControl.setValue(this.currentDisplayLabel(), {
      emitEvent: false,
    });
  }

  private currentDisplayLabel(): string {
    if (
      this.emptyLabel &&
      (this.selectedValue === this.emptyValue ||
        this.selectedValue === null ||
        this.selectedValue === undefined ||
        this.selectedValue === "")
    ) {
      return this.emptyLabel;
    }
    const selected = (this.options ?? []).find(
      (item) => this.valueWith(item) === this.selectedValue,
    );
    return selected ? this.displayWith(selected) : "";
  }

  private refreshFiltered(): void {
    const items = [...(this.options ?? [])];
    const raw = this.searchControl.value
      .trim()
      .toLowerCase()
      .replace(/^\+/, "");
    const currentLabel = this.currentDisplayLabel().toLowerCase();
    const query = !raw || raw === currentLabel ? "" : raw;
    this.filtered = query
      ? items.filter((item) => {
          const searchable = (
            this.searchWith?.(item) ?? this.displayWith(item)
          ).toLowerCase();
          return searchable.includes(query);
        })
      : items;
  }
}
